import { Divider, EditableText } from '@blueprintjs/core';
import { showSaveSuccessToast } from 'Components/Generic/BOSToasterUtil';
import { useAlertContext } from 'Data/context/Alerts';
import GenericTableData from 'Data/GenericTableData';
import DataManager from 'Lib/DataManager';
import { useUpdatableObject } from 'Lib/hooks/useUpdatableObject';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { formatDate, formatDateTime } from 'Util/dateUtil';
import DeleteButton from '../Buttons/DeleteButton';
import SaveButton from '../Buttons/SaveButton';

let DM = new DataManager();

const NoteCard = ({
  noteObject,
  editable = false,
  dataIdentifier,
  notesTableName,
  noteType = '',
  newNoteFieldValues = {},
  onUpdateNote = () => {},
  onSaveNote = () => {},
  onDeleteNote = () => {},
}) => {
  const { openDeleteDialog, showDeletedToast } = useAlertContext();

  const NotesDataRef = useRef(
    GenericTableData.getTableDataBus(dataIdentifier, notesTableName),
  );
  let NotesData = NotesDataRef.current;

  const {
    currentObject: note,
    hasBeenUpdated,
    reset: resetNote,
    updateField,
    doSave,
  } = useUpdatableObject(NotesData.updateRecord, NotesData.createRecord);
  const noteRef = useRef();
  noteRef.current = note;

  const [contentIsEditing, setContentIsEditing] = useState(false);

  useEffect(() => {
    if (NotesData) {
      DM.add(
        NotesData.registerListener('RECORD_LOADED', onNoteLoaded),
        NotesData,
      );
      DM.add(
        NotesData.registerListener('RECORD_UPDATED', onNoteUpdated),
        NotesData,
      );
      DM.add(
        NotesData.registerListener('RECORD_CREATED', onNoteCreated),
        NotesData,
      );
      DM.add(
        NotesData.registerListener('RECORDS_DELETED', onNoteDeleted),
        NotesData,
      );
    }
  }, []);

  useEffect(() => {
    if (note && Object.keys(note).length > 0) {
      setContentIsEditing(!note.id);
    }
  }, [note]);

  useEffect(() => {
    if (noteObject) {
      let newNote = { ...noteObject, ...newNoteFieldValues };
      resetNote(newNote);
    }
  }, [noteObject]);

  const onNoteLoaded = (payload) => {
    if (payload) {
      let newNote = { ...payload, ...newNoteFieldValues };
      resetNote(newNote);
    }
  };

  useEffect(() => {
    if (hasBeenUpdated) {
      onUpdateNote(hasBeenUpdated);
    }
  }, [hasBeenUpdated]);

  const updateNoteText = (newValue) => {
    updateField('content', newValue);
  };

  const onNoteCreated = (payload) => {
    onNoteSaved(true, payload);
  };

  const onNoteUpdated = (payload) => {
    onNoteSaved(false, payload);
  };

  const onNoteSaved = (isNew, updatedNote) => {
    onSaveNote(isNew, updatedNote);
    resetNote(updatedNote);
    showSaveSuccessToast(
      `${noteType ? noteType + ' ' : ''}Note ${
        isNew ? 'Created and ' : ''
      }Saved`,
    );
  };
  const deleteClicked = () => {
    if (note.id) {
      openDeleteDialog({
        objectType: `${noteType ? noteType + ' ' : ''}Note`,
        onConfirmDelete: () => NotesData.deleteRecords([noteRef.current.id]),
      });
    } else {
      onDeleteNote();
    }
  };

  const onNoteDeleted = () => {
    onDeleteNote();
    showDeletedToast(`${noteType ? noteType + ' ' : ''}Note`);
  };

  return (
    <div
      key={note.id || `${note.user_id}-${note.createdAt}`}
      style={{
        width: '100%',
        padding: '10px 0',
        minWidth: 360,
      }}>
      <div
        style={{
          marginLeft: 10,
        }}>
        <span
          style={{
            fontWeight: 600,
            color: 'var(--primary-bos-color)',
            marginRight: 6,
          }}>
          {note.user?.full_name || 'unknown'}
        </span>
        <span
          style={{
            fontStyle: 'italic',
            fontSize: 'small',
            color: 'var(--bp-gray3)',
          }}>
          {`${
            note.createdAt
              ? formatDateTime(note.createdAt)
              : formatDate(new Date())
          }`}
        </span>
      </div>
      <Divider />
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div
          style={{
            flexGrow: 1,
            marginLeft: 10,
            marginRight: 6,
            whiteSpace: 'pre-line',
          }}>
          {(editable || !!note.content) && (
            <EditableText
              defaultValue={note.content}
              isEditing={contentIsEditing}
              multiline
              disabled={note.id && !editable}
              onChange={updateNoteText}
            />
          )}
        </div>
        {(!note.id || editable) && (
          <DeleteButton
            miniVersion
            onDeleteItem={deleteClicked}
            tooltipText={'Delete Note'}
          />
        )}
        {hasBeenUpdated && (
          <SaveButton
            miniVersion
            disabled={!note?.content}
            clickHandler={doSave}
          />
        )}
      </div>
    </div>
  );
};

NoteCard.propTypes = {
  noteObject: PropTypes.object,
  editable: PropTypes.bool,
  dataIdentifier: PropTypes.string,
  notesTableName: PropTypes.string,
  noteType: PropTypes.string,
  newNoteFieldValues: PropTypes.object,
  onUpdateNote: PropTypes.func,
  onSaveNote: PropTypes.func,
  onDeleteNote: PropTypes.func,
};

export default NoteCard;
